import { addDays, addHours, addMonths, addWeeks, addYears } from 'date-fns';
import { IS_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import { EPersonalSharingMode } from 'reactApp/components/SharingNewBiz/Sharing/SharingNewSharing.helpers';

import {
    type DomainAccessOptions,
    type IAutoDeleteOptions,
    type IExpiresOptions,
    type IGetDescriptionProps,
    type IGetTitleProps,
    AccessStatus,
    AutoDeletePeriod,
    DomainAccess,
    DownloadAccess,
    ExpiryPeriod,
} from './SharingNew.types';

export const DOWNLOAD_FILE_OPTIONS = {
    [DownloadAccess.Limited]: {
        id: DownloadAccess.Limited,
        text: 'с ограничением',
        dropdownText: 'С ограничением',
    },
    [DownloadAccess.Unlimited]: {
        id: DownloadAccess.Unlimited,
        text: 'без ограничений',
        dropdownText: 'Без ограничений',
    },
};

export const DOWNLOAD_FILE_LIMIT_ACTIVE_OPTIONS = {
    [DownloadAccess.Limited]: {
        id: DownloadAccess.Limited,
        dropdownText: 'Изменить',
    },
    [DownloadAccess.Unlimited]: {
        id: DownloadAccess.Unlimited,
        dropdownText: 'Снять ограничения',
    },
};

export const ACCESS_FILE_RIGHTS_OPTIONS_BIZ = {
    [AccessStatus.ReadOnly]: {
        id: AccessStatus.ReadOnly,
        text: 'смотреть',
        dropdownText: 'Только смотреть',
    },
    [AccessStatus.Download]: {
        id: AccessStatus.Download,
        text: 'смотреть и скачивать',
        dropdownText: 'Смотреть и скачивать',
    },
    [AccessStatus.Edit]: {
        id: AccessStatus.Edit,
        text: 'редактировать',
        dropdownText: 'Редактировать',
    },
};

export const ACCESS_FILE_RIGHTS_OPTIONS_ONPREMISE = {
    [AccessStatus.ReadOnly]: {
        id: AccessStatus.ReadOnly,
        text: 'Просмотр',
    },
    [AccessStatus.Download]: {
        id: AccessStatus.Download,
        text: 'Скачивание',
    },
    [AccessStatus.Edit]: {
        id: AccessStatus.Edit,
        text: 'Редактирование',
    },
};

export const FREE_BIZ_SAAS_USER_ACCESS_OPTIONS = {
    [AccessStatus.ReadOnly]: ACCESS_FILE_RIGHTS_OPTIONS_BIZ.ReadOnly,
    [AccessStatus.Download]: ACCESS_FILE_RIGHTS_OPTIONS_BIZ.Download,
};

export const ACCESS_FOLDER_RIGHTS_OPTIONS_ONPREMISE = {
    [AccessStatus.ReadOnly]: {
        id: AccessStatus.ReadOnly,
        text: 'Просмотр',
    },
    [AccessStatus.Download]: {
        id: AccessStatus.Download,
        text: 'Скачивание',
    },
    [AccessStatus.Edit]: {
        id: AccessStatus.Edit,
        text: 'Редактирование',
    },
};

export const ACCESS_FOLDER_RIGHTS_OPTIONS_BIZ = {
    [AccessStatus.ReadOnly]: {
        id: AccessStatus.ReadOnly,
        text: 'смотреть',
        dropdownText: 'Только смотреть',
    },
    [AccessStatus.Download]: {
        id: AccessStatus.Download,
        text: 'смотреть и скачивать',
        dropdownText: 'Смотреть и скачивать',
    },
    [AccessStatus.Edit]: {
        id: AccessStatus.Edit,
        text: 'редактировать',
        dropdownText: 'Редактировать',
    },
};

export const EXPIRES_OPTIONS: Record<string, IExpiresOptions> = {
    [ExpiryPeriod.Unlimited]: {
        id: ExpiryPeriod.Unlimited,
        text: 'Всегда',
        getValue: () => 0,
        timeSize: 0,
    },
    [ExpiryPeriod.Hour]: {
        id: ExpiryPeriod.Hour,
        text: 'Час',
        getValue: (): number => addHours(new Date(), 1).valueOf(),
        timeSize: addHours(new Date(), 1).valueOf() - Date.now(),
    },
    [ExpiryPeriod.Day]: {
        id: ExpiryPeriod.Day,
        text: 'День',
        getValue: (): number => addDays(new Date(), 1).valueOf(),
        timeSize: addDays(new Date(), 1).valueOf() - Date.now(),
    },
    [ExpiryPeriod.Week]: {
        id: ExpiryPeriod.Week,
        text: 'Неделя',
        getValue: (): number => addWeeks(new Date(), 1).valueOf(),
        timeSize: addWeeks(new Date(), 1).valueOf() - Date.now(),
    },
    [ExpiryPeriod.Month]: {
        id: ExpiryPeriod.Month,
        text: 'Месяц',
        getValue: (): number => addMonths(new Date(), 1).valueOf(),
        timeSize: addMonths(new Date(), 1).valueOf() - Date.now(),
    },
    [ExpiryPeriod.Months3]: {
        id: ExpiryPeriod.Months3,
        text: '3 месяца',
        getValue: (): number => addMonths(new Date(), 3).valueOf(),
        timeSize: addMonths(new Date(), 3).valueOf() - Date.now(),
    },
    [ExpiryPeriod.HalfYear]: {
        id: ExpiryPeriod.HalfYear,
        text: 'Полгода',
        getValue: (): number => addMonths(new Date(), 6).valueOf(),
        timeSize: addMonths(new Date(), 6).valueOf() - Date.now(),
    },
    [ExpiryPeriod.Year]: {
        id: ExpiryPeriod.Year,
        text: 'Год',
        getValue: (): number => addYears(new Date(), 1).valueOf(),
        timeSize: addYears(new Date(), 1).valueOf() - Date.now(),
    },
};

export const AUTODELETE_OPTIONS: Record<string, IAutoDeleteOptions> = {
    [AutoDeletePeriod.None]: {
        id: AutoDeletePeriod.None,
        text: 'Не удалять',
        getValue: () => 0,
    },
    [AutoDeletePeriod.Day]: {
        id: AutoDeletePeriod.Day,
        text: 'Удалить через день',
        getValue: (): number => addDays(new Date(), 1).valueOf(),
    },
    [AutoDeletePeriod.Week]: {
        id: AutoDeletePeriod.Week,
        text: 'Удалить через неделю',
        getValue: (): number => addWeeks(new Date(), 1).valueOf(),
    },
    [AutoDeletePeriod.Month]: {
        id: AutoDeletePeriod.Month,
        text: 'Удалить через месяц',
        getValue: (): number => addMonths(new Date(), 1).valueOf(),
    },
    [AutoDeletePeriod.Months3]: {
        id: AutoDeletePeriod.Months3,
        text: 'Удалить через 3 месяца',
        getValue: (): number => addMonths(new Date(), 3).valueOf(),
    },
    [AutoDeletePeriod.HalfYear]: {
        id: AutoDeletePeriod.HalfYear,
        text: 'Удалить через полгода',
        getValue: (): number => addMonths(new Date(), 6).valueOf(),
    },
    [AutoDeletePeriod.Year]: {
        id: AutoDeletePeriod.Year,
        text: 'Удалить через год',
        getValue: (): number => addYears(new Date(), 1).valueOf(),
    },
};

export const domainAccessOptions: Record<DomainAccess, DomainAccessOptions> = {
    [DomainAccess.EXTERNAL]: {
        id: DomainAccess.EXTERNAL,
        text: 'Для всех пользователей',
        dropdownText: 'Всем',
    },
    [DomainAccess.DOMESTIC]: {
        id: DomainAccess.DOMESTIC,
        text: `Для пользователей домена`,
        dropdownText: 'Коллегам',
    },
    [DomainAccess.EMAIL_LIST_ACCESS]: {
        id: DomainAccess.EMAIL_LIST_ACCESS,
        text: 'Никому',
        dropdownText: 'Добавленным пользователям',
    },
};

export enum QA_VALUE {
    accessItem = 'item_access',
    accessDropdown = 'dropdown_access',
    accessDropdownContent = 'dropdownContent_access',
    downloadDropdownContent = 'dropdownContent_download',
    expiresItem = 'item_expires',
    expiresDropdown = 'dropdown_expires',
    expiresDropdownContent = 'dropdownContent_expires',
    domesticItem = 'item_domestic',
    domesticDropdown = 'dropdown_domestic',
    domesticDropdownContent = 'dropdownContent_domestic',
    autoDeleteItem = 'item_autoDelete',
    autoDeleteDropdown = 'dropdown_autoDelete',
    autoDeleteDropdownContent = 'dropdownContent_autoDelete',
    downloadItem = 'item_download',
    passwordItem = 'item_password',
}

export const AB_FEATURE_NAME = 'new-sharing';

export const TOOLTIP_OFFSET = { y: 4 };

export const getTitle = (props: IGetTitleProps): string => {
    const { isPublic, isMounted, name, showUsersScreen } = props;

    if (IS_BIZ_USER && showUsersScreen) {
        return `Персональный доступ к «${name}»`;
    }

    if (IS_BIZ_USER) {
        return `Доступ к «${name}»`;
    }

    if (isPublic) {
        return 'Совместный доступ';
    }

    if (isMounted) {
        return `Доступ к папке «${name}»`;
    }

    return 'Включить совместный доступ';
};

export const getUsersScreenDescription = ({ isFolder, personalSharingMode }: IGetDescriptionProps) => {
    if (isFolder) {
        return personalSharingMode === EPersonalSharingMode.mount
            ? 'Пользователь должен принять доступ к папке в разделе «С вами поделились». Папка отобразится в Диске пользователя и будет занимать там место, если он не в вашем домене'
            : 'Настройте персональный доступ, затем отправьте пользователю ссылку на папку. Папка не появится в его Диске, а будет доступна только по ссылке';
    }

    return 'Настройте персональный доступ, затем отправьте пользователю ссылку на файл';
};
